import type {
  AttributeInfo,
  ParticipantsDistribution,
  RoomGroup,
  RoomInfo,
  RoomSelectorResponse,
  SpecialAgeProfile,
  Stock,
} from '@/scopes/prices/domain/rooms/types'
import type { Attribute, Package, Room, RoomSelectorApiRawResponse } from '@/scopes/prices/infrastructure/rooms/types'

export interface RoomSelectorResponseMapper {
  map: (roomSelectorResponse: RoomSelectorApiRawResponse) => RoomSelectorResponse
}

export function createRoomSelectorResponseMapper(): RoomSelectorResponseMapper {
  return {
    map: (roomSelectorResponse: RoomSelectorApiRawResponse): RoomSelectorResponse => {
      const uniqueRoomNumbers = new Set(roomSelectorResponse.data.packages.map(p => p.roomNumber))

      const roomGroups = Array.from(uniqueRoomNumbers).map((roomNumber) => {
        const packagesGroupedByRoomId = getPackagesGroupedByRoomId(roomSelectorResponse.data.packages, roomNumber)
        const cheapestPackagesPerRoomId = getCheapestPackagesPerRoomId(packagesGroupedByRoomId)

        const rooms = cheapestPackagesPerRoomId.map((p) => {
          const roomInfo = roomSelectorResponse.data.rooms.find(r => r.id === p.roomId)
          if (!roomInfo)
            return null
          return fillRoomInfo(roomInfo, p)
        })

        const participants = roomSelectorResponse.data.participants[Number(roomNumber) - 1]
        const distribution = mapRoomDistribution(participants, roomSelectorResponse.data.participantsProfiles)

        return {
          roomNumber: roomNumber.toString(),
          rooms,
          distribution,
        } as RoomGroup
      })

      const stock = roomSelectorResponse.data.rooms.reduce<Stock>((acc: Stock, room): Stock => {
        if (!acc[room.id]) {
          acc[room.id] = room.stock
        }

        return acc
      }, {})

      const orderedRoomGroups = orderRooms(roomGroups)

      const specialAgeProfiles = getSpecialAgeProfiles(roomSelectorResponse)

      return { roomGroups: orderedRoomGroups, stock, specialAgeProfiles }
    },
  }
}

function orderRooms(roomGroups: RoomGroup[]) {
  return roomGroups.sort((a, b) => {
    const roomNumberA = Number(a.roomNumber)
    const roomNumberB = Number(b.roomNumber)
    return roomNumberA - roomNumberB
  })
}

function fillRoomInfo(roomInfo: Room, pckg: Package): RoomInfo {
  return {
    id: roomInfo.id,
    name: roomInfo.name,
    subtitle: roomInfo.subtitle,
    characteristics: roomInfo.characteristics || [],
    price: pckg.price,
    totalPrice: pckg.totalPrice,
    contractId: pckg.contractId,
    images: fillImages(roomInfo),
    facilities: mapAttributes(roomInfo.facilities),
    onRequest: pckg.onRequest,
  }
}

function fillImages(roomInfo: Room) {
  return (
    roomInfo.images?.map(image => ({
      src: image.galleryImageOriginalImage?.resolvedSrc?.split('?')[0] || '',
      alt: image.galleryImageOriginalImage?.alt || '',
    })) || []
  )
}

function mapAttributes(attributes: Attribute[] | undefined): AttributeInfo[] {
  return attributes?.filter(attribute => !!attribute.caption)
    .map(attribute => ({
      code: attribute.code,
      caption: attribute.caption!,
    })) || []
}

function getPackagesGroupedByRoomId(packages: Package[], roomNumber: number) {
  const packagePerRoomNumber = packages.filter(p => p.roomNumber === roomNumber)
  return packagePerRoomNumber.reduce<Record<string, Package[]>>((acc, pkg: Package) => {
    if (!acc[pkg.roomId]) {
      acc[pkg.roomId] = [pkg]
    }
    else {
      acc[pkg.roomId].push(pkg)
    }
    return acc
  }, {})
}

function getCheapestPackagesPerRoomId(packagesGroupedByRoomId: Record<string, Package[]>) {
  return Object.values(packagesGroupedByRoomId).map(packages =>
    packages.reduce((cheapest, current) => (current.price < cheapest.price ? current : cheapest)),
  )
}

function mapRoomDistribution(
  participants: string[],
  participantsProfiles: Record<string, string>,
): ParticipantsDistribution {
  const distribution = {
    adults: 0,
    children: 0,
    babies: 0,
  }

  participants.forEach((participant) => {
    const profile = participantsProfiles[participant]
    if (profile === 'adults') {
      distribution.adults++
    }
    else if (profile === 'children') {
      distribution.children++
    }
    else if (profile === 'babies') {
      distribution.babies++
    }
  })

  return distribution
}

function getSpecialAgeProfiles(roomSelectorResponse: RoomSelectorApiRawResponse) {
  if (!roomSelectorResponse.data.isSpecialAgeProfile) {
    return null
  }

  return roomSelectorResponse.data.ageProfiles
    .filter(ageProfile => ageProfile.minAge !== undefined)
    .reduce((acc: Record<string, SpecialAgeProfile>, ageProfile) => {
      acc[ageProfile.id] = {
        minAge: ageProfile.minAge,
        maxAge: ageProfile.maxAge,
      }
      return acc
    }, {})
}
